import { memo, useEffect, useRef, type CSSProperties } from "react";
import { GALAXY_BASE, GALAXY_RINGS } from "@/lib/galaxy_data";
import { useReducedMotion } from "@/lib/motion";

/**
 * Jake's spiral galaxy (mobile/src/newtopic/new_topic_screen.tsx) in React DOM: ten ring SVGs spinning at
 * their own periods inside a tilted, flattened disc (rotate -24deg, scaleY .56), with a breathing core.
 * Each ring is an <img> drawn once; one rAF loop only writes transforms. `speed` multiplies the ring
 * periods (1 idle, .18 charting, like the app) and is eased toward, so a phase change feels like a spin-up.
 */
const RING_SRC = GALAXY_RINGS.map(xml => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`);

export const Galaxy = memo(function Galaxy({
  size,
  speed = 1,
  glow = 0,
  pulse = 0,
  className = "",
  style,
}: {
  /** Diameter of the (unrotated) disc, in px. */
  size: number;
  /** Ring period multiplier: lower spins faster. */
  speed?: number;
  /** 0..1: how hard the core and halo shine (charting, brief). */
  glow?: number;
  /** Change it to send one pulse out from the core. */
  pulse?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const reduced = useReducedMotion();
  const root = useRef<HTMLDivElement>(null);
  const rings = useRef<(HTMLImageElement | null)[]>([]);
  const target = useRef(speed);
  target.current = reduced ? Math.max(speed, 1) * 6 : speed;

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    let raf = 0;
    let last = 0;
    let tau = Math.random() * 40;
    let cur = target.current;
    let visible = true;
    const frame = (now: number) => {
      const dt = last ? Math.min(0.1, (now - last) / 1000) : 0;
      last = now;
      cur += (target.current - cur) * Math.min(1, dt * 2.5);
      tau += dt / cur;
      rings.current.forEach((r, k) => {
        if (r) r.style.transform = `rotate(${(((tau / GALAXY_BASE[k]!) % 1) * 360).toFixed(2)}deg)`;
      });
      raf = visible ? requestAnimationFrame(frame) : 0;
    };
    // Only spin while on screen.
    const io = new IntersectionObserver(([e]) => {
      visible = !!e?.isIntersecting;
      if (visible && !raf) {
        last = 0;
        raf = requestAnimationFrame(frame);
      }
    });
    io.observe(el);
    raf = requestAnimationFrame(frame);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  const s = size / 440;
  return (
    <div ref={root} aria-hidden className={`pointer-events-none relative select-none ${className}`} style={{ width: size, height: size, ...style }}>
      {/* Halo behind the disc: brighter while charting. */}
      <div
        className="absolute inset-[-12%] rounded-full transition-opacity duration-700"
        style={{
          background: "radial-gradient(closest-side, rgba(122,77,255,0.45), rgba(59,59,255,0.14) 55%, transparent 75%)",
          opacity: 0.35 + 0.65 * glow,
        }}
      />
      <div className="absolute inset-0" style={{ transform: "rotate(-24deg) scaleY(0.56)" }}>
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(122,77,255,0.55), rgba(59,59,255,0.22) 55%, rgba(27,42,168,0) 100%)" }}
        />
        {RING_SRC.map((src, k) => (
          <img
            key={k}
            ref={r => {
              rings.current[k] = r;
            }}
            src={src}
            alt=""
            draggable={false}
            className="absolute inset-0 h-full w-full"
            style={{ willChange: "transform" }}
          />
        ))}
        {/* Breathing core (4 s) and its hot centre. */}
        <div
          className="la-breathe absolute rounded-full"
          style={{
            left: 174 * s,
            top: 174 * s,
            width: 92 * s,
            height: 92 * s,
            background: "radial-gradient(closest-side, #FFFFFF, #FFF4C8 35%, rgba(255,200,90,0.6) 70%, rgba(255,138,77,0) 100%)",
            transform: `scale(${1 + glow * 0.35})`,
            transition: "transform 700ms cubic-bezier(.3,1,.4,1)",
          }}
        />
        <div className="absolute rounded-full bg-white" style={{ left: 208 * s, top: 208 * s, width: 24 * s, height: 24 * s }} />
        {pulse > 0 && !reduced && (
          <div
            key={pulse}
            className="la-ring-pulse absolute rounded-full"
            style={{ left: "50%", top: "50%", width: size * 0.3, height: size * 0.3, marginLeft: -size * 0.15, marginTop: -size * 0.15 }}
          />
        )}
      </div>
    </div>
  );
});
